import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { TabsProvider } from "../../context/tabsContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminFinesApi } from "../../http/fines/api";
import { UpsertFine } from "../../http/fines/types";
import FineAttachments from "../fineAttachments/FineAttachments";
import Header from "../ui/Header";
import Tabs, { TabContent, TabsContents } from "../ui/Tabs";
import FineForm, { FineFormBody } from "./FineForm";
import FineNotifications from "./FineNotifications";

const EditFine: FC = () => {
  const i18n = useI18n();

  return (
    <>
      <Header title={i18n.t.fines.edit} />
      <TabsProvider>
        <TabbedEditFine />
      </TabsProvider>
    </>
  );
};

export default EditFine;

const TabbedEditFine: FC = () => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const finesApi = useAdminFinesApi();
  const [isDragging, setIsDragging] = useState(false);
  const { id: fineId } = useParams<{ id: string }>();

  const [params] = useSearchParams();
  const carId = params.get("car_id");

  const fineSwr = finesApi.getAdminFine(fineId!, aborter.current.signal);
  const [fine, isInitialized] = useDataInitializer(fineSwr.data, fineSwr.error);

  useEffect(() => {
    fineId && fineSwr.trigger();
  }, [fineId]);

  useEffect(() => {
    fineSwr.error && dispatch(showErrorNotification(i18n, fineSwr.error));
  }, [fineSwr.error]);

  const onSubmit = async (formData: FineFormBody): Promise<void> => {
    if (fine?.id) {
      setIsSubmitting(true);

      const body: UpsertFine = {
        carId: carId || formData.car?.id || "",
        driverId: formData.driver?.id ?? "",
        subsidiaryId: formData.subsidiary?.id ?? "",
        status: formData.status.value,
        verbalNumber: formData.verbalNumber,
        violationDate: formData.violationDate,
        communicationDate: formData.communicationDate,
        amount: formData.amount ?? undefined,
        amountCurrency: formData.amountCurrency?.value,
        points: formData.points ?? undefined,
        accountantId: formData.accountant?.id ?? null,
        hrReferentId: formData.hrReferent?.id ?? null,
        note: formData.note,
        currentVersionNumber: fine.versionNumber,
      };

      try {
        await finesApi.updateAdminFine(fine.id, body);
        dispatch(showSuccessNotification(i18n.t));
        await fineSwr.trigger();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const tabs = [{ title: i18n.t.common.generalData }, { title: i18n.t.fines.notifications }, { title: i18n.t.common.documents }];

  const tabComponents: TabContent[] = [
    {
      body: (
        <FineForm
          onSubmit={onSubmit}
          isDisabled={!userSettings.permissions.fines.update || isSubmitting || !fine?.id}
          isLoading={!isInitialized}
          fine={fine}
          carId={carId}
        />
      ),
      withBorder: true,
      eager: true,
    },
    {
      body: <FineNotifications fine={fine} onUpdate={() => fineSwr.trigger()} />,
      withBorder: true,
    },
    {
      body: <FineAttachments setIsDragging={setIsDragging} />,
      withBorder: true,
    },
  ];

  return (
    <>
      <Tabs tabs={tabs} />
      <TabsContents contents={tabComponents} isDragging={isDragging} />
    </>
  );
};
